import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

/**
 * Ends impersonation: restores the admin's backed-up accessToken/user and clears impersonation
 * storage. Throws if there's nothing to restore (impersonation wasn't active on this device).
 */
export function useEndImpersonation(): UseMutationResult<void, Error, void> {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const originalAccessToken = (await StorageHandler.load(storageKeys.impersonationOriginalAccessToken)) as
        | string
        | false;

      if (!originalAccessToken) {
        throw new Error('No original session stored — cannot end impersonation');
      }

      type StoredUser = Record<string, unknown> | false;
      const originalUser = (await StorageHandler.load(storageKeys.impersonationOriginalUser)) as StoredUser;

      StorageHandler.save(storageKeys.accessToken, originalAccessToken);
      if (originalUser) {
        StorageHandler.save(storageKeys.user, originalUser);
      }

      StorageHandler.removeKeys([storageKeys.impersonationOriginalAccessToken, storageKeys.impersonationOriginalUser]);
      StorageHandler.save(storageKeys.impersonationState, {
        active: false,
        endedAt: new Date().toISOString(),
      });
    },
  });
}
